<script setup>
import { XIcon } from "lucide-vue-next";
import SimpleBar from "simplebar-vue";
import { ref } from "vue";

import InfiniteLoading from "@/Components/InfiniteLoading.vue";

import useCampaignDrawer from "@/composables/campaignDrawer";
import CommentForm from "@campaign-js/Components/CommentForm.vue";
import CommentItem from "@campaign-js/Components/CommentItem.vue";
import { usePageComment } from "@campaign-js/composables/pageComment";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  page: {
    type: Object,
    required: true
  },
  isLoggedIn: {
    type: Boolean,
    required: true
  }
});

const drawer = useCampaignDrawer("page:comment");
const { paginate, infiniteId, isProcessing, fetch, createComment, updateComment, deleteComment } = usePageComment(props.campaign.team, props.page);

const addComment = (newComment) => {
  if (paginate.data.length === 0) {
    paginate.data = [newComment];
    paginate.total = 1;
  } else {
    paginate.data.push(newComment);
    paginate.total++;
  }
};

const simpleBarRef = ref(null);

const hideDrawer = () => {
  const scrollElement = simpleBarRef.value?.scrollElement;

  if (scrollElement && scrollElement.scrollTop === 0) {
    drawer.hide();
  }
};
</script>

<template>
  <transition
    enter-active-class="ease-out transform-gpu transition-transform duration-200"
    enter-from-class="translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="ease-in transform-gpu transition-transform duration-200"
    leave-from-class="translate-y-0"
    leave-to-class="translate-y-full"
  >
    <div
      v-if="drawer.isOpen"
      class="campaign-drawer no-padding fixed left-0 z-50 w-full rounded-t-xl bg-[#fff] shadow-soft isp-bottom-0 sm:left-auto sm:w-campaign sm:max-w-campaign"
      v-bind="$attrs"
    >
      <div class="relative">
        <div v-touch:swipe.bottom="hideDrawer" class="flex h-full flex-col">
          <div class="flex-1">
            <div class="flex justify-between border-b border-zinc-300 p-4">
              <h2 class="font-bold text-zinc-900">
                댓글 <span v-if="paginate.total > 0">{{ paginate.total }}</span>
              </h2>

              <div>
                <XIcon class="h-6 w-6 cursor-pointer text-zinc-600 hover:text-zinc-900" @click="drawer.hide()" />
              </div>
            </div>

            <div class="relative">
              <SimpleBar ref="simpleBarRef" class="comment-bar h-[calc(70vh-160px)] overflow-y-auto py-4 scrollbar-hide">
                <div>
                  <div v-if="paginate.total === 0" class="flex h-40 items-center justify-center text-sm+">
                    <p class="text-600">아직 댓글이 없습니다.</p>
                  </div>

                  <CommentItem
                    v-for="item in paginate.data"
                    :key="item.hashid"
                    :campaign="campaign"
                    :page="page"
                    :comment="item"
                    :is-logged-in="isLoggedIn"
                    :on-create="createComment"
                    :on-update="updateComment"
                    :on-delete="deleteComment"
                  />

                  <InfiniteLoading :identifier="infiniteId" @infinite="fetch" />
                </div>
              </SimpleBar>
            </div>
          </div>

          <CommentForm
            :campaign="campaign"
            :page="page"
            :is-logged-in="isLoggedIn"
            :is-processing="isProcessing"
            :on-create="createComment"
            @created="addComment"
          />
        </div>
      </div>
    </div>
  </transition>
</template>
